import { useState, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { bulkImportClients, downloadBulkImportTemplate } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Download,
  Upload,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Users,
  RefreshCw,
} from "lucide-react";

const REQUIRED_COLUMNS = [
  "Agency Reseller Partner",
  "Rep Email",
  "Client Business Name",
];

const OPTIONAL_COLUMNS = [
  "Rep Name",
  "Client Contact Email",
  "Client Phone",
  "Marketing Products",
  "Web Design/Hosting Products",
  "SEO/AEO Products",
  "ProspectID/ProspectMax Products",
  "Monthly Contract Value",
  "Auto-Renew",
  "Renewal Date",
  "Notes",
];

export default function BulkImportPage() {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Super Admin only
  if (user && user.role !== "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  const handleFile = (selected) => {
    if (!selected) return;
    const lower = selected.name.toLowerCase();
    if (!lower.endsWith(".csv")) {
      toast.error("Please upload a .csv file (you can export from Google Sheets or Excel)");
      return;
    }
    setFile(selected);
    setResult(null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const downloadTemplate = async () => {
    try {
      const response = await downloadBulkImportTemplate();
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "bulk_import_template.csv";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Failed to download template");
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Choose a CSV file first");
      return;
    }
    setUploading(true);
    setResult(null);
    try {
      const { data } = await bulkImportClients(file);
      setResult(data);
      if (data.clients_created > 0) {
        toast.success(`Imported ${data.clients_created} client${data.clients_created === 1 ? "" : "s"}`);
      } else {
        toast.warning("No clients were imported. See the report below.");
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Bulk import failed";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-8" data-testid="bulk-import-page">
      <div>
        <h1 className="font-outfit text-3xl font-bold tracking-tight">Bulk Import Clients</h1>
        <p className="text-muted-foreground mt-1">
          Pre-load existing recurring clients for your reseller partners. Imported clients land in
          <span className="font-medium text-foreground"> Monthly Active Clients</span> with no welcome emails sent.
        </p>
      </div>

      {/* Step 1: Template */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            Step 1 — Download the template
          </CardTitle>
          <CardDescription>
            Use the template as your starting point, then export from Google Sheets / Excel as <code className="px-1 rounded bg-muted text-xs">.csv</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={downloadTemplate} variant="outline" data-testid="download-template-btn">
            <Download className="h-4 w-4 mr-2" /> Download CSV Template
          </Button>

          <div className="grid gap-4 md:grid-cols-2 pt-2">
            <div>
              <p className="text-sm font-semibold mb-2">Required columns</p>
              <div className="flex flex-wrap gap-2">
                {REQUIRED_COLUMNS.map((c) => (
                  <Badge key={c} variant="default">{c}</Badge>
                ))}
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold mb-2">Optional columns</p>
              <div className="flex flex-wrap gap-2">
                {OPTIONAL_COLUMNS.map((c) => (
                  <Badge key={c} variant="outline">{c}</Badge>
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-md bg-muted/40 px-4 py-3 text-xs text-muted-foreground space-y-1">
            <p><strong>Tip:</strong> Multiple services in the same column? Separate them with commas. Example: <code className="text-foreground">Google Ads, Facebook</code></p>
            <p><strong>Renewal Date:</strong> Use <code className="text-foreground">YYYY-MM-DD</code> or <code className="text-foreground">M/D/YYYY</code>. Leave blank if unknown.</p>
            <p><strong>Auto-create:</strong> Missing agencies and reps are created automatically. Reps get a temp password (no email is sent — share access manually or use Admin → Reset Password).</p>
            <p><strong>Free-text products:</strong> Service names that don't exactly match the catalog are kept verbatim in the right product bucket and appended to the project notes for later cleanup.</p>
          </div>
        </CardContent>
      </Card>

      {/* Step 2: Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Upload className="h-5 w-5 text-primary" />
            Step 2 — Upload your CSV
          </CardTitle>
          <CardDescription>
            Lifecycle is set to <strong>Monthly Active</strong>; no onboarding tasks or welcome emails are generated.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div
            onDrop={onDrop}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors cursor-pointer ${
              dragOver ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:border-primary/40"
            }`}
            onClick={() => fileInputRef.current?.click()}
            data-testid="bulk-import-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="bulk-import-file-input"
            />
            <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
            {file ? (
              <div>
                <p className="font-medium">{file.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{(file.size / 1024).toFixed(1)} KB — Click to choose a different file</p>
              </div>
            ) : (
              <div>
                <p className="text-sm font-medium">Drag your CSV here or click to browse</p>
                <p className="text-xs text-muted-foreground mt-1">.csv files only</p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            {file && (
              <Button variant="ghost" onClick={reset} disabled={uploading} data-testid="bulk-import-reset-btn">
                Clear
              </Button>
            )}
            <Button
              onClick={handleUpload}
              disabled={!file || uploading}
              data-testid="bulk-import-submit-btn"
            >
              {uploading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing…</>
              ) : (
                <><Users className="h-4 w-4 mr-2" /> Import Clients</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Step 3: Result */}
      {result && (
        <Card data-testid="bulk-import-result">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              Import Report
            </CardTitle>
            <CardDescription>
              Processed {result.total_rows} row{result.total_rows === 1 ? "" : "s"} from <span className="font-medium text-foreground">{file?.name}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-3 gap-4">
              <div className="rounded-md border p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">{result.clients_created}</p>
                <p className="text-xs text-muted-foreground mt-1">Clients created</p>
              </div>
              <div className="rounded-md border p-4 text-center">
                <p className="text-2xl font-bold text-primary">{result.projects_created}</p>
                <p className="text-xs text-muted-foreground mt-1">Active projects</p>
              </div>
              <div className="rounded-md border p-4 text-center">
                <p className="text-2xl font-bold text-amber-600">{result.skipped}</p>
                <p className="text-xs text-muted-foreground mt-1">Rows skipped</p>
              </div>
            </div>

            {result.created?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm mb-3">Imported clients</h3>
                <div className="rounded-md border divide-y">
                  {result.created.map((c, i) => (
                    <div key={i} className="px-4 py-3 flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{c.client}</p>
                        <p className="text-xs text-muted-foreground">
                          {c.agency} · {c.rep_email}
                        </p>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {c.sub_products?.map((sp, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">{sp}</Badge>
                          ))}
                        </div>
                      </div>
                      <div className="text-right text-xs whitespace-nowrap text-muted-foreground">
                        {c.contract_value != null && <div>${Number(c.contract_value).toLocaleString()}/mo</div>}
                        {c.renewal_date && <div>Renews {c.renewal_date}</div>}
                        {c.auto_renew && <Badge className="mt-1" variant="secondary">Auto-Renew</Badge>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {result.warnings?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-sky-700">
                  <AlertCircle className="h-4 w-4" /> Notices ({result.warnings.length})
                </h3>
                <div className="rounded-md border border-sky-200 bg-sky-50/40 divide-y divide-sky-100 max-h-72 overflow-y-auto">
                  {result.warnings.map((w, i) => (
                    <div key={i} className="px-4 py-2 text-sm">
                      <span className="font-medium">Row {w.row}:</span>{" "}
                      <span className="text-foreground/80">{w.reason}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Notices are non-blocking — the rows still imported. Free-text services are stored on the project and listed in its notes.
                </p>
              </div>
            )}

            {result.errors?.length > 0 && (
              <div>
                <h3 className="font-semibold text-sm mb-3 flex items-center gap-2 text-amber-700">
                  <AlertCircle className="h-4 w-4" /> Issues ({result.errors.length})
                </h3>
                <div className="rounded-md border border-amber-200 bg-amber-50/40 divide-y divide-amber-100 max-h-72 overflow-y-auto">
                  {result.errors.map((err, i) => (
                    <div key={i} className="px-4 py-2 text-sm">
                      <span className="font-medium">Row {err.row}:</span>{" "}
                      <span className="text-foreground/80">{err.reason}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Fix these in your spreadsheet, save as CSV again, and re-upload — already-imported rows will be created as new entries (no automatic dedupe).
                </p>
              </div>
            )}

            <div className="flex justify-end">
              <Button variant="outline" onClick={reset} data-testid="bulk-import-import-another">
                <RefreshCw className="h-4 w-4 mr-2" /> Import Another File
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
